import type { Voucher } from '@/lib/redux/features/voucher/type'

const DB_NAME = 'traveleasy-voucher-pwa'
const DB_VERSION = 1
const PUBLIC_STORE = 'public-voucher-lists'
const CUSTOMER_STORE = 'customer-voucher-lists'
const VOUCHER_LIST_TTL_MS = 60 * 60 * 1000

type SnapshotRecord<T> = {
  key: string
  data: T
  savedAt: number
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined'
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(new Error('Could not open voucher snapshot database'))
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(PUBLIC_STORE)) {
        db.createObjectStore(PUBLIC_STORE, { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains(CUSTOMER_STORE)) {
        db.createObjectStore(CUSTOMER_STORE, { keyPath: 'key' })
      }
    }
  })
}

function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(storeName, mode)
        const store = transaction.objectStore(storeName)
        const request = fn(store)

        request.onsuccess = () => resolve(request.result as T)
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
        transaction.oncomplete = () => db.close()
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'))
      }),
  )
}

async function readSnapshot<T>(storeName: string, key: string): Promise<SnapshotRecord<T> | null> {
  const record = await withStore<SnapshotRecord<T> | undefined>(storeName, 'readonly', (store) =>
    store.get(key),
  )

  if (!record) {
    return null
  }

  if (Date.now() - record.savedAt > VOUCHER_LIST_TTL_MS) {
    await withStore(storeName, 'readwrite', (store) => store.delete(key))
    return null
  }

  return record
}

async function writeSnapshot<T>(storeName: string, key: string, data: T): Promise<void> {
  const record: SnapshotRecord<T> = {
    key,
    data,
    savedAt: Date.now(),
  }

  await withStore(storeName, 'readwrite', (store) => store.put(record))
}

export async function savePublicVoucherListSnapshot(key: string, data: Voucher[]): Promise<void> {
  if (!isBrowser() || !data.length) {
    return
  }

  await writeSnapshot(PUBLIC_STORE, key, data)
}

export async function getPublicVoucherListSnapshot(key: string): Promise<SnapshotRecord<Voucher[]> | null> {
  if (!isBrowser()) {
    return null
  }

  return readSnapshot<Voucher[]>(PUBLIC_STORE, key)
}

export async function saveCustomerVoucherSnapshot(customerCode: string, data: Voucher[]): Promise<void> {
  if (!isBrowser() || !customerCode) {
    return
  }

  await writeSnapshot(CUSTOMER_STORE, customerCode, data)
}

export async function getCustomerVoucherSnapshot(
  customerCode: string,
): Promise<SnapshotRecord<Voucher[]> | null> {
  if (!isBrowser() || !customerCode) {
    return null
  }

  return readSnapshot<Voucher[]>(CUSTOMER_STORE, customerCode)
}
